import { useEffect, useMemo, useState } from 'react';
import type {
  CreateDeliveryConditionReasonModel,
  DeliveryConditionReasonAdminDto,
  UpdateDeliveryConditionReasonModel,
} from 'api/model';
import {
  useGlobalSettingsCreateDeliveryConditionReason,
  useGlobalSettingsGetDeliveryConditionReasons,
  useGlobalSettingsUpdateDeliveryConditionReason,
} from 'api/global-settings/global-settings';
import useMe from 'contexts/authentication/useMe';
import Roles, { includesRoles } from 'constants/Roles';
import styled from 'styled-components';
import { faPlus, faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showException } from 'utils/exception-helper';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import Input from 'components/inputs/Input';
import Checkbox from 'components/inputs/Checkbox';
import TextButton from 'components/inputs/TextButton';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CellRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NewRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  padding-top: 10px;
  border-top: 1px solid #eee;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #555;
`;

const InactiveText = styled.span`
  color: #999;
  font-style: italic;
`;

interface ReasonRow {
  id: number;
  key: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
}

interface PendingEdit {
  label?: string;
  sortOrder?: number;
  isActive?: boolean;
}

const KEY_PATTERN = /^[a-z0-9_]+$/;

const DeliveryConditionReasonSettings = () => {
  const me = useMe();
  const isSuperAdmin = includesRoles([Roles.SuperAdmin], me?.roles);
  const { data: reasons, isLoading, refetch } =
    useGlobalSettingsGetDeliveryConditionReasons();
  const updateCall = useGlobalSettingsUpdateDeliveryConditionReason();
  const createCall = useGlobalSettingsCreateDeliveryConditionReason();

  const [edits, setEdits] = useState<Record<number, PendingEdit>>({});
  const [savingRows, setSavingRows] = useState<Set<number>>(new Set());
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newSortOrder, setNewSortOrder] = useState<number>(0);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setEdits({});
    // Förbered nästa SortOrder automatiskt så admin slipper räkna —
    // default = max existing + 1.
    const nextSort = (reasons ?? []).reduce((max, r) => Math.max(max, r.sortOrder), 0) + 1;
    setNewSortOrder(nextSort);
  }, [reasons]);

  const rows = useMemo((): ReasonRow[] => {
    return (reasons ?? [])
      .slice()
      .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id)
      .map((r: DeliveryConditionReasonAdminDto) => ({
        id: r.id,
        key: r.key,
        label: r.label,
        sortOrder: r.sortOrder,
        isActive: r.isActive,
      }));
  }, [reasons]);

  const getDisplay = (row: ReasonRow) => ({
    label: edits[row.id]?.label ?? row.label,
    sortOrder: edits[row.id]?.sortOrder ?? row.sortOrder,
    isActive: edits[row.id]?.isActive ?? row.isActive,
  });

  const isChanged = (row: ReasonRow) => {
    const d = getDisplay(row);
    return (
      d.label !== row.label ||
      d.sortOrder !== row.sortOrder ||
      d.isActive !== row.isActive
    );
  };

  const setField = <K extends keyof PendingEdit>(
    id: number,
    field: K,
    value: PendingEdit[K]
  ) => {
    setEdits((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleSave = async (row: ReasonRow) => {
    const d = getDisplay(row);
    if (!d.label.trim()) {
      showException(new Error('Label får inte vara tom.'));
      return;
    }
    const data: UpdateDeliveryConditionReasonModel = {
      reasonID: row.id,
      label: d.label.trim(),
      sortOrder: d.sortOrder,
      isActive: d.isActive,
    };
    setSavingRows((prev) => new Set(prev).add(row.id));
    try {
      await updateCall.mutateAsync({ data });
      refetch();
    } catch (error) {
      showException(error);
    } finally {
      setSavingRows((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
    }
  };

  const handleCreate = async () => {
    const key = newKey.trim();
    const label = newLabel.trim();
    if (!KEY_PATTERN.test(key)) {
      showException(
        new Error(
          'Key måste vara små bokstäver (a-z), siffror eller understreck.'
        )
      );
      return;
    }
    if (!label) {
      showException(new Error('Label krävs.'));
      return;
    }
    const data: CreateDeliveryConditionReasonModel = {
      key,
      label,
      sortOrder: newSortOrder,
    };
    setCreating(true);
    try {
      await createCall.mutateAsync({ data });
      setNewKey('');
      setNewLabel('');
      refetch();
    } catch (error) {
      showException(error);
    } finally {
      setCreating(false);
    }
  };

  const columnSettings = useMemo(
    (): ColumnSetting<ReasonRow, object>[] => [
      {
        head: 'Ordning',
        width: 90,
        render: (row) => (
          <Input
            type="number"
            min={0}
            small
            width={70}
            disabled={!isSuperAdmin}
            value={getDisplay(row).sortOrder}
            onChange={(e) =>
              setField(row.id, 'sortOrder', Number(e.target.value))
            }
          />
        ),
      },
      {
        head: 'Key',
        width: 240,
        render: (row) =>
          row.isActive ? (
            <code>{row.key}</code>
          ) : (
            <InactiveText>
              <code>{row.key}</code>
            </InactiveText>
          ),
      },
      {
        head: 'Label (visas i appen)',
        width: 380,
        render: (row) => (
          <Input
            type="text"
            small
            width={360}
            disabled={!isSuperAdmin}
            value={getDisplay(row).label}
            onChange={(e) => setField(row.id, 'label', e.target.value)}
          />
        ),
      },
      {
        head: 'Aktiv',
        width: 80,
        render: (row) => (
          <Checkbox
            checked={getDisplay(row).isActive}
            disabled={!isSuperAdmin}
            onChange={(e) =>
              setField(row.id, 'isActive', e.currentTarget.checked)
            }
          />
        ),
      },
      ...(isSuperAdmin
        ? [
            {
              head: '',
              width: 50,
              render: (row: ReasonRow) => (
                <CellRow>
                  {savingRows.has(row.id) ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : isChanged(row) ? (
                    <TextButton onClick={() => handleSave(row)}>
                      <FontAwesomeIcon icon={faSave} />
                    </TextButton>
                  ) : null}
                </CellRow>
              ),
            },
          ]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, edits, savingRows, isSuperAdmin]
  );

  if (isLoading) {
    return <FontAwesomeIcon icon={faSpinner} spin />;
  }

  return (
    <Wrapper>
      <h1>
        Leveransskick — anledningar{' '}
        {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
      </h1>
      <p>
        Standardiserade anledningar som FT kan markera vid &quot;Ej godtagbart
        leveransskick&quot;. Inaktiverade anledningar visas inte i mobil-appen
        men sparas för historisk KPI-aggregering. <strong>Key</strong> är en
        stabil identifierare som inte kan ändras efter skapande — byt inte den
        även om Label översätts. Vill du byta key, inaktivera den gamla och
        skapa en ny.
      </p>
      <Table fluid columnSettings={columnSettings} rows={rows} />

      {isSuperAdmin ? (
        <NewRow>
          <Field>
            <span>Key (snake_case, a–z/0–9/_)</span>
            <Input
              type="text"
              small
              width={220}
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder="ex: torn_seat"
            />
          </Field>
          <Field>
            <span>Label (visas i appen)</span>
            <Input
              type="text"
              small
              width={360}
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="t.ex. Trasig stol"
            />
          </Field>
          <Field>
            <span>Ordning</span>
            <Input
              type="number"
              min={0}
              small
              width={80}
              value={newSortOrder}
              onChange={(e) => setNewSortOrder(Number(e.target.value))}
            />
          </Field>
          <TextButton onClick={handleCreate} disabled={creating}>
            {creating ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} /> Lägg till anledning
              </>
            )}
          </TextButton>
        </NewRow>
      ) : null}
    </Wrapper>
  );
};

export default DeliveryConditionReasonSettings;
